/* eslint-disable jsx-a11y/anchor-is-valid */
/*
 * This is the stuff that lives on top of a scratchpad during a challenge.
 */
import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import {compose} from "mu-lambda";
import * as React from "react";

import {ModalLauncher} from "@khanacademy/wonder-blocks-modal";
import {color} from "@khanacademy/wonder-blocks-tokens";
import {RequestInfo} from "@khan/shared-core";

import withUserProfile, {
    type WithoutUserProfile,
    type WithUserProfileProps,
} from "#data-access/with-user-profile.tsx";
import {moduleLayout} from "#modules-package/styles.ts";
import withBackboneModels from "#react-components-package/with-backbone-models.tsx";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedSpinner from "#shared-components-package/deprecated-spinner.tsx";

import ChallengeFeedbackModal from "./challenge-feedback-modal.tsx";
import ChallengeStep from "./challenge-step.tsx";
import {
    getColoredTextForTest,
    sendContentFeedback,
    type ScratchpadChallenge,
} from "./scratchpad-challenge.ts";

type Props = {
    // The challenge we're currently dealing with
    // @ts-expect-error - TS2749 - 'ScratchpadChallenge' refers to a value, but is being used as a type here. Did you mean 'typeof ScratchpadChallenge'?
    challenge: ScratchpadChallenge;
    // The title of the scratchpad
    translatedTitle: string;
} & WithUserProfileProps;

type State = {
    showFeedbackModal: boolean;
};

type Test = {
    name: string;
    results: Array<{
        msg: string;
        meta: {
            image: string;
            alsoMessage?: string;
            alternateMessage?: string;
        };
    }>;
};

class ChallengePane extends React.Component<Props, State> {
    // @ts-expect-error - TS2564 - Property '_testsCache' has no initializer and is not definitely assigned in the constructor.
    _testsCache: Array<Test>;

    state: State = {
        showFeedbackModal: false,
    };

    getTestGroup(): Test | null | undefined {
        const challenge = this.props.challenge;
        let tests: Array<Test> = challenge.get("tests");

        // NOTE(jeresig): We cache the tests in case there is a syntax error
        // when we attempt to run the validation tests (which would cause no
        // tests to be created)
        if (challenge.hasSyntaxErrors()) {
            if (this._testsCache) {
                tests = this._testsCache;
            } else {
                // Unfortunately, if we hit a syntax error without ever having
                // run the validation tests, it's not currently possible to
                // know which instructions the user should see. (That is, if
                // the challenge was saved and subsequently loaded with a
                // syntax error, we can't know which step the user was on when
                // they last edited the challenge code.) In that case, we
                // fallback to displaying an error message (by handling this
                // `null` at the callsite).
                return null;
            }
        }

        this._testsCache = tests;

        return tests[Math.min(challenge.get("step"), tests.length - 1)];
    }

    handleOpenFeedbackModal: () => void = () =>
        this.setState({showFeedbackModal: true});

    handleCloseFeedbackModal: () => void = () =>
        this.setState({showFeedbackModal: false});

    renderShowFeedback(): React.ReactNode {
        const {challenge, profile} = this.props;
        const expectPageIsTranslated = ["es", "pt"].includes(
            RequestInfo.kaLocale,
        );

        return (
            <React.Fragment>
                <a
                    className={css(styles.showFeedback)}
                    href="javascript:void(0)"
                    onClick={this.handleOpenFeedbackModal}
                >
                    {t`Report a problem`}
                </a>
                <ModalLauncher
                    opened={this.state.showFeedbackModal}
                    onClose={this.handleCloseFeedbackModal}
                    modal={({closeModal}) => (
                        <ChallengeFeedbackModal
                            expectPageIsTranslated={expectPageIsTranslated}
                            closeModal={closeModal}
                            sendFeedback={(...args) =>
                                sendContentFeedback(challenge, profile, ...args)
                            }
                            stepCodeWarning={challenge.currentStepHasWarning()}
                            stepGoBackNotice={challenge.previousStepFailed()}
                        />
                    )}
                />
            </React.Fragment>
        );
    }

    renderSteps(): React.ReactNode {
        const challenge = this.props.challenge;
        const editorType = challenge.editor.type;

        const testGroup = this.getTestGroup();
        if (testGroup) {
            return (
                <div>
                    {testGroup.results.map((test, i) => {
                        const structure = getColoredTextForTest(test);
                        const hasMatching =
                            structure.indexOf("structuredjs_var") > -1;
                        const alsoMessage =
                            test.meta.alsoMessage ||
                            (challenge.assertionsFail() &&
                                t`Hm, do all of your assertions pass?`) ||
                            undefined;
                        return (
                            <ChallengeStep
                                key={i}
                                title={testGroup.name}
                                description={test.msg}
                                hasHint={!!(structure || test.meta.image)}
                                structure={structure}
                                image={test.meta.image}
                                alternateMessage={test.meta.alternateMessage}
                                alsoMessage={alsoMessage}
                                hasMatchingBlanks={hasMatching}
                                isPJS={editorType === "ace_pjs"}
                                isWebpage={editorType === "ace_webpage"}
                                hasSyntaxErrors={challenge.hasSyntaxErrors()}
                            />
                        );
                    })}
                </div>
            );
        }
        // If we have no testGroup, then the challenge was loaded with an
        // existing syntax error. See: `getTestGroup()`.
        return (
            <div>
                <ChallengeStep
                    description={t`It looks like this code was saved with a syntax error. You'll need to fix any syntax errors or restart the challenge before we can determine which step you're on.`}
                    structure=""
                    alsoMessage={t`Please take a moment to fix any syntax errors.`}
                    isPJS={editorType === "ace_pjs"}
                    isWebpage={editorType === "ace_webpage"}
                    title={t`Whoops!`}
                />
            </div>
        );
    }

    renderLoading(): React.ReactNode {
        return (
            <div className={css(styles.loading)}>
                <DeprecatedSpinner />
                <div>{t`Waiting for code to run...`}</div>
            </div>
        );
    }

    renderContent(): React.ReactNode {
        return (
            <div>
                {this.renderShowFeedback()}
                {this.renderSteps()}
            </div>
        );
    }

    render(): React.ReactNode {
        const challenge = this.props.challenge;
        return (
            <div>
                <div className={css(styles.wrap)}>
                    {challenge.get("tests")
                        ? this.renderContent()
                        : this.renderLoading()}
                </div>
            </div>
        );
    }
}

const styles = StyleSheet.create({
    loading: {
        textAlign: "center",
        padding: 20,
    },
    showFeedback: {
        color: color.offBlack64,
        top: 10,
        fontSize: 10,
        position: "absolute",
        right: 10,
        textDecoration: "none",
    },
    wrap: {
        ...moduleLayout.defaultAlignment,
        color: "black",
        // Cancel out the margin provided by scratchpad-header
        // ...it's getting unncessarily roomy down here.
        marginBottom: -20,
        // We don't want the standard module padding...
        // that's way too much!
        padding: 20,
        paddingBottom: 0,
        position: "relative",
    },
});

type ExportProps = WithoutUserProfile<
    JSX.LibraryManagedAttributes<
        typeof ChallengePane,
        React.ComponentProps<typeof ChallengePane>
    >
>;

export default compose(
    withBackboneModels((props) => [props.challenge]),
    withUserProfile,
)(ChallengePane) as React.ComponentType<ExportProps>;
